import {AR_FOV_H, createModelLayer, headingPitchFromDevice} from "./ar_model"
import {pinNdc, posterHalfNdc, posterSpansView} from "./ar_look.js"
import {CampaignAudio} from "./campaign_audio.js"
import {createPoseFilter} from "./pose_filter"
import {
  DEFAULT_SIZE_ACTIVATION,
  DEFAULT_SIZE_DETECTION,
  EARTH_M,
  aglForZone,
  createStickyZone,
  facingForZone,
  haversineM,
  initialBearingDeg,
  containFill,
  exclusionOwner,
  lingerAllowed,
  lingerSeconds,
  overlayVisible,
  pinFlag,
  radiiFromPin,
  seekAllowed,
  sizeMForZone,
  sizeModeForZone,
  skyRigEnu,
  tapHintZone,
  tapToPlay,
} from "./ar_zones"
import {floorAltM, heightAboveFloorM} from "./venue_alt"
import {SPEED_WARN, nextSpeedLocked} from "./ar_speed.js"
import {REDACT_WARN, pointInRedactPolygons} from "./ar_redact.js"
import {attachQa} from "./ar_qa.js"
import {CAMERA_MAX_REVIVES, cameraPictureOk, sampleVideoLuma} from "./ar_camera.js"
import {remember as rememberSeam} from "./handoff_seam.js"
import {recoveryMessage, shouldRepaint, shouldStopRetrying} from "./camera_paint.js"
import {
  GEO_DENIED_SETTINGS_URL,
  isIosSafari,
  shouldOfferSettings,
} from "./ar_geo_denied.js"
import {
  GPS_COARSE_MAX_M,
  GPS_COARSE_WAIT_MS,
  GPS_STALE_HUD,
  GPS_TIGHT_M,
  SKY_WAIT_NEAR,
  gpsAccuracyM,
  gpsCoord,
  gpsCoordsOk,
  gpsFixOk,
  gpsHudApproximate,
  gpsIgnoreSpike,
  gpsLikelyIndoors,
  gpsSkyHint,
  gpsStale,
  gpsLiveAgeMs,
  gpsSampleAgeMs,
  gpsPositionTimestampMs,
  gpsSeedSampleOk,
  shouldPrewarmWatch,
  shouldReviveWatch,
  shouldSkipArGate,
  startGeolocation,
  usableWatchId,
} from "./ar_gps.js"

function lingerOr(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, n) : fallback
}

function pinTitle(pin) {
  if (typeof pin.hud_title === "string" && pin.hud_title.trim()) return pin.hud_title
  const name = pin.name || "Pin"
  const label = typeof pin.label === "string" ? pin.label.trim() : ""
  return label ? `${name} — ${label}` : name
}

export const ARViewer = {
  mounted() {
    this.video = this.el.querySelector("#ar-video")
    this.canvas = this.el.querySelector("#ar-canvas")
    this.glCanvas = this.el.querySelector("#ar-gl")
    this.ctx = this.canvas.getContext("2d", {alpha: true})
    this.gate = this.el.querySelector("#ar-gate")
    this.stage = this.el.querySelector("#ar-stage")
    this.startBtn = this.el.querySelector("#start-btn")
    this.startLabel = this.el.querySelector("#start-btn-label")
    this.errorEl = this.el.querySelector("#ar-error")
    this.rangeBanner = this.el.querySelector("#ar-range-banner")
    this.zoneChip = this.el.querySelector("#ar-zone-chip")
    this.rangeText = this.el.querySelector("#ar-range-text")
    this.cameraRecoverBtn = this.el.querySelector("#ar-camera-recover")
    this.stopBtn = this.el.querySelector("#ar-stop")
    this.seek = this.el.querySelector("#ar-seek")
    this.seekArrow = this.el.querySelector("#ar-seek-arrow")
    this.seekLabel = this.el.querySelector("#ar-seek-label")
    this.hudCompass = this.el.querySelector("#ar-hud-compass")
    this.hudGps = this.el.querySelector("#ar-hud-gps")
    this.hudNearest = this.el.querySelector("#ar-hud-nearest")
    this.hudDiag = this.el.querySelector("#ar-dev-diag")
    this.hudHint = this.el.querySelector("#ar-hud-hint")
    this.lockEl = this.el.querySelector("#ar-lock")
    this.lockKicker = this.el.querySelector("#ar-lock-kicker")
    this.lockTitle = this.el.querySelector("#ar-lock-title")
    this.lockBody = this.el.querySelector("#ar-lock-body")
    this.speedWarnEl = this.el.querySelector("#ar-speed-warn")
    if (this.speedWarnEl) this.speedWarnEl.textContent = SPEED_WARN
    this.pageEl = this.el.closest(".ar-page")

    this.running = false
    this._started = false
    this._tornDown = false
    this._ios = this.isIOS()
    this._gotAbsolute = false
    this._lookingAt = undefined
    this.myLat = null
    this.myLng = null
    this.myAlt = null
    this.altAccuracy = null
    this._groundAlt = null
    this.accuracy = null
    this.heading = null
    this.pitch = 0
    this.poseFilter = createPoseFilter()
    this._speedLocked = false
    this.speedMps = null
    this.watchId = null
    this.vidStream = null
    this._cameraReviving = false
    this._cameraMuteTimer = 0
    this._cameraReviveCount = 0
    this._cameraRepaints = 0
    this._cameraRepainting = false
    this._cameraStuck = false
    this._cameraStartedAt = 0
    this._camLuma = null
    this._camProbe = document.createElement("canvas")
    this._camProbe.width = 32
    this._camProbe.height = 32
    this._camProbeCtx = this._camProbe.getContext("2d", {willReadFrequently: true})
    this._lastHudAt = 0
    this._absoluteTimer = null
    this._geoError = null
    this.pins = this.readPins()
    this._pinsDataset = this.el.dataset.pins || "[]"
    this.lookSeconds = Number(this.el.dataset.lookSeconds || 3)
    this.handoffHoldSeconds = Number(this.el.dataset.handoffHoldSeconds || 2)
    this.handoffFlySeconds = Number(this.el.dataset.handoffFlySeconds || 0.7)
    this._stickyById = new Map()
    this._geoById = new Map()
    this._kraxtisKind = new Map()
    this.diagnostics =
      this.el.dataset.arDiag === "true" || this.el.dataset.arDiagnostics === "true"
    this._webgl = true
    this.modelLayer = null
    this._glbPinId = null
    this._dwell = new Map()
    this._activateSent = false
    this._handoff = null
    this._playStarted = new Set()
    this._closeLock = new Map()
    this._bestAcc = null
    this._bestAccAt = 0
    this._gpsStartedAt = null
    this._liveGps = false
    this._fixAgeMs = Infinity
    this._lastFixAt = 0
    this._watchArmedAt = 0
    this._hiddenAt = 0
    this._gpsNeedsSeed = false
    this._geoStop = null
    this._geoPermStatus = null
    this.dwellGraceMs = 300
    this._qa = attachQa(this)

    this.onStartClick = (e) => {
      e.preventDefault()
      this.startExploring()
    }
    this.onAbsolute = (e) => this.handleOrientation(e, true)
    this.onOrientation = (e) => this.handleOrientation(e, false)
    this.onMotion = (e) => this.handleMotion(e)
    this.onCalibration = () =>
      this.setHint("Move the phone in a figure-8 to calibrate the compass.")
    this.onResize = () => this.resizeCanvas()
    this.markHidden = () => {
      if (!this._hiddenAt) this._hiddenAt = Date.now()
    }
    this.takeHiddenMs = () => {
      const ms = this._hiddenAt ? Date.now() - this._hiddenAt : 0
      this._hiddenAt = 0
      return ms
    }
    this.onForeground = () => {
      this.resizeCanvas()
      this.reviveGeolocation({
        evenIfDenied: true,
        reason: "foreground",
        hiddenMs: this.takeHiddenMs(),
      })
      if (this._started) this.reviveCamera()
    }
    this.onPageShow = (event) => {
      this.resizeCanvas()
      this.reviveGeolocation({
        evenIfDenied: true,
        reason: event?.persisted ? "bfcache" : "foreground",
        hiddenMs: this.takeHiddenMs(),
      })
      if (this._started) this.reviveCamera()
    }
    this.onPageHide = () => {
      this._qa?.onPageHide()
      this.markHidden()
    }
    this.onVisibility = () => {
      if (document.visibilityState === "hidden") {
        this.markHidden()
        return
      }
      if (document.visibilityState === "visible") this.onForeground()
    }

    this.startBtn.addEventListener("click", this.onStartClick)
    this.stopBtn?.addEventListener("click", (event) => {
      event.preventDefault()
      this.stopSession()
    })
    this.cameraRecoverBtn?.addEventListener("click", (event) => {
      event.preventDefault()
      this.reviveCamera({force: true})
    })
    // Delegated, because the sheet lives outside `#ar-root` (LiveView may
    // replace it) and per-button listeners here would leak on every remount.
    this._onGeoDeniedClick = (event) => {
      if (!event.target?.closest?.("[data-ar-geo-denied-close]")) return
      event.preventDefault()
      this.closeLocationSettings()
    }
    document.addEventListener("click", this._onGeoDeniedClick)
    this._onGeoDeniedKey = (event) => {
      if (event.key !== "Escape" || !this._geoDeniedOpen) return
      event.preventDefault()
      this.closeLocationSettings()
    }
    window.addEventListener("keydown", this._onGeoDeniedKey)
    window.addEventListener("resize", this.onResize)
    window.addEventListener("orientationchange", this.onResize)
    window.addEventListener("pageshow", this.onPageShow)
    window.addEventListener("pagehide", this.onPageHide)
    document.addEventListener("visibilitychange", this.onVisibility)
    this.resizeCanvas()

    if (!this.isAllowedOrigin()) {
      this.showError(
        "Safari blocks camera and GPS on plain HTTP. Open the https:// address from this machine."
      )
      this.startBtn.disabled = true
      return
    }

    this.handleEvent("pins", ({pins}) => {
      this.pins = this.normalizePins(pins).filter((pin) => pin.enabled)
      this._stickyById = new Map()
      this._geoById = new Map()
      this._kraxtisKind = new Map()
      const ids = new Set(this.pins.map((pin) => String(pin.id)))
      for (const id of this._dwell.keys()) {
        if (!ids.has(id)) this._dwell.delete(id)
      }
      this.preloadPinVideos()
    })

    this.ensureModelLayer()
    this.preloadPinVideos()
    this.prewarmGeolocation()
  },

  updated() {
    const raw = this.el.dataset.pins || "[]"
    if (raw === this._pinsDataset) return
    this._pinsDataset = raw
    const next = this.readPins()
    const key = next
      .map(
        (pin) =>
          `${pin.id}:${pin.exclusion_radius_m}:${pin.activation_radius_m}:${pin.detection_radius_m}:${pin.beacon_radius_m}:${pin.remote_radius_m}:${pin.remote_finite}`,
      )
      .join("|")
    this.pins = next
    if (this._pinRadiiKey && this._pinRadiiKey !== key) {
      this._stickyById = new Map()
      this._geoById = new Map()
    }
    this._pinRadiiKey = key
    this.preloadPinVideos()
  },

  destroyed() {
    this.teardown()
  },

  disconnected() {
    // LiveView sockets drop often on phones. Do not kill camera/GPS/compass.
  },

  reconnected() {
    if (this._started) this.reviveGeolocation({reason: "reconnect"})
  },

  isIOS() {
    return (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function"
    )
  },

  readPins() {
    try {
      return this.normalizePins(JSON.parse(this.el.dataset.pins || "[]")).filter(
        (pin) => pin.enabled
      )
    } catch {
      return []
    }
  },

  normalizePins(pins) {
    if (!Array.isArray(pins)) return []

    return pins.map((pin) => ({
      ...pin,
      id: pin.id,
      event_id: pin.event_id,
      enabled: pinFlag(pin.enabled),
      label: typeof pin.label === "string" ? pin.label : "",
      hud_title:
        typeof pin.hud_title === "string" && pin.hud_title.trim()
          ? pin.hud_title
          : pinTitle(pin),
      lat: Number(pin.lat),
      lng: Number(pin.lng),
      exclusion_radius_m: Number(pin.exclusion_radius_m) || 0,
      activation_radius_m: Number(pin.activation_radius_m) || 0,
      detection_radius_m: Number(pin.detection_radius_m) || 0,
      beacon_radius_m: Number(pin.beacon_radius_m) || 0,
      remote_radius_m: Number(pin.remote_radius_m) || 0,
      remote_finite: pinFlag(pin.remote_finite),
      altitude_agl_activation: Number(pin.altitude_agl_activation) || 0,
      altitude_agl_detection: Number(pin.altitude_agl_detection) || 0,
      altitude_agl_remote: Number(pin.altitude_agl_remote) || 0,
      model_key: pin.model_key || null,
      model_url: pin.model_url || pin.mosaic_url || null,
      mosaic_url: pin.mosaic_url || null,
      overlay_kind:
        pin.overlay_kind === "mp4"
          ? "mp4"
          : pin.overlay_kind === "texture"
            ? "texture"
            : pin.overlay_kind === "glb"
              ? "glb"
              : pin.mosaic_url
                ? "texture"
                : null,
      model_facing: pin.model_facing === "world" ? "world" : "billboard",
      model_yaw_deg: Number(pin.model_yaw_deg) || 0,
      apparent_size_activation:
        Number(pin.apparent_size_activation) > 0
          ? Number(pin.apparent_size_activation)
          : DEFAULT_SIZE_ACTIVATION,
      apparent_size_detection:
        Number(pin.apparent_size_detection) > 0
          ? Number(pin.apparent_size_detection)
          : DEFAULT_SIZE_DETECTION,
      beacon_viewport_fraction: Number(pin.beacon_viewport_fraction) || 0.36,
      remote_viewport_fraction: Number(pin.remote_viewport_fraction) || 0.36,
      lock_messages: Array.isArray(pin.lock_messages)
        ? pin.lock_messages.filter((line) => typeof line === "string" && line.trim())
        : [],
      redact_polygons: Array.isArray(pin.redact_polygons) ? pin.redact_polygons : [],
      linger_seconds_activation: lingerOr(pin.linger_seconds_activation, 3),
      linger_seconds_detection: lingerOr(pin.linger_seconds_detection, 0),
      linger_seconds_beacon: lingerOr(pin.linger_seconds_beacon, 0),
      linger_seconds_remote: lingerOr(pin.linger_seconds_remote, 0),
      tap_to_play_activation: pin.tap_to_play_activation == null ? true : pinFlag(pin.tap_to_play_activation),
      tap_to_play_detection: pin.tap_to_play_detection == null ? true : pinFlag(pin.tap_to_play_detection),
      tap_to_play_beacon: pin.tap_to_play_beacon == null ? true : pinFlag(pin.tap_to_play_beacon),
      tap_to_play_remote: pin.tap_to_play_remote == null ? true : pinFlag(pin.tap_to_play_remote),
      handoff_contain_fraction: containFill(pin),
      debug: pinFlag(pin.debug),
      play_before_handoff_seconds: Math.max(0, Number(pin.play_before_handoff_seconds) || 2),
      handoff_hold_seconds: Math.max(0, Number(pin.handoff_hold_seconds) || 2),
      handoff_fly_seconds: Math.max(0.05, Number(pin.handoff_fly_seconds) || 0.7),
    }))
  },

  isAllowedOrigin() {
    if (window.isSecureContext) return true
    return location.hostname === "localhost" || location.hostname === "127.0.0.1"
  },

  sessionReadyKey() {
    return "moon_scan_ar_ready"
  },

  lastFixKey() {
    return "moon_scan_last_fix"
  },

  gpsTargetAccuracyM() {
    return GPS_TIGHT_M
  },

  lastFixMaxAgeMs() {
    return 2 * 60 * 60 * 1000
  },

  sessionReady() {
    try {
      return sessionStorage.getItem(this.sessionReadyKey()) === "1"
    } catch {
      return false
    }
  },

  markSessionReady() {
    try {
      sessionStorage.setItem(this.sessionReadyKey(), "1")
    } catch {
      // Private mode may block sessionStorage.
    }
  },

  clearSessionReady() {
    try {
      sessionStorage.removeItem(this.sessionReadyKey())
    } catch {
      // ignore
    }
  },

  readLastFix() {
    const parse = (raw) => {
      if (!raw) return null
      const data = JSON.parse(raw)
      const lat = Number(data.lat)
      const lng = Number(data.lng)
      const acc = Number(data.acc)
      const at = Number(data.at)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
      if (!Number.isFinite(at) || Date.now() - at > this.lastFixMaxAgeMs()) return null
      return {lat, lng, acc: Number.isFinite(acc) ? acc : 40, at}
    }

    try {
      return parse(sessionStorage.getItem(this.lastFixKey())) || parse(localStorage.getItem(this.lastFixKey()))
    } catch {
      return null
    }
  },

  writeLastFix(lat, lng, acc) {
    const payload = JSON.stringify({lat, lng, acc, at: Date.now()})
    try {
      sessionStorage.setItem(this.lastFixKey(), payload)
    } catch {
      // ignore
    }
    try {
      localStorage.setItem(this.lastFixKey(), payload)
    } catch {
      // ignore
    }
  },

  prewarmGeolocation() {
    if (!this.isAllowedOrigin() || !navigator.geolocation) return

    this._onGeoPermChange = () => {
      if (this._tornDown) return
      if (shouldPrewarmWatch(this._geoPermStatus?.state)) this.kickoffGeolocation()
    }

    const maybeSkipGate = () => {
      if (this._tornDown || this._started) return
      if (!this.sessionReady()) return
      CampaignAudio.armGestureUnlock()
      if (shouldSkipArGate({sessionReady: true, permState: this._geoPermStatus?.state})) {
        this.startExploring()
      } else {
        this.setStartLabel("Continue exploring")
      }
    }

    const perms = navigator.permissions
    if (!perms?.query) {
      maybeSkipGate()
      return
    }
    perms
      .query({name: "geolocation"})
      .then((status) => {
        if (this._tornDown) return
        this._geoPermStatus = status
        if (shouldPrewarmWatch(status.state)) this.kickoffGeolocation()
        status.addEventListener?.("change", this._onGeoPermChange)
        maybeSkipGate()
      })
      .catch(() => {
        maybeSkipGate()
      })
  },

  gpsElapsedMs() {
    if (!this._gpsStartedAt) return 0
    return Date.now() - this._gpsStartedAt
  },

  gpsLikelyIndoors() {
    return gpsLikelyIndoors({
      accuracy: this.accuracy,
      elapsedMs: this.gpsElapsedMs(),
      bestAcc: this._bestAcc,
      sinceImproveMs: Date.now() - (this._bestAccAt || 0),
    })
  },

  // At the venue (inside the outermost *finite* ring of any enabled pin) a
  // fresh coarse fix is accepted at once instead of after the 25 s street
  // ladder. Unbounded remote means "the whole planet", so it must not count:
  // a fan 7 km away is not at the venue.
  atVenue() {
    if (this.myLat == null || this.myLng == null) return false
    return this.pins.some((pin) => {
      const outer = pinFlag(pin.remote_finite)
        ? Number(pin.remote_radius_m) || 0
        : Number(pin.beacon_radius_m) || 0
      const d = this.pinDistance(pin)
      return Number.isFinite(d) && d <= Math.max(outer, Number(pin.activation_radius_m) || 0)
    })
  },

  // Age of the fix *now*, not when it arrived. `_fixAgeMs` is a receipt
  // snapshot: without the elapsed part a dead watch reports a fresh fix
  // forever, which is what the corpus shows (silent 112 s, age 2.3 s).
  fixAgeMs() {
    if (!Number.isFinite(this._fixAgeMs)) return Infinity
    if (!this._lastFixAt) return this._fixAgeMs
    return gpsLiveAgeMs(this._fixAgeMs, Date.now() - this._lastFixAt)
  },

  gpsSampleOk() {
    return gpsFixOk({
      accuracy: this.accuracy,
      liveGps: this._liveGps,
      elapsedMs: this.gpsElapsedMs(),
      ageMs: this.fixAgeMs(),
      silentMs: this.geoSilentMs(),
      venue: this.atVenue(),
      speedMps: this.speedMps,
    })
  },

  gpsApproximate() {
    return gpsHudApproximate({
      accuracy: this.accuracy,
      liveGps: this._liveGps,
      elapsedMs: this.gpsElapsedMs(),
      ageMs: this.fixAgeMs(),
      silentMs: this.geoSilentMs(),
      venue: this.atVenue(),
      speedMps: this.speedMps,
    })
  },

  gpsOverlayOk() {
    if (this._handoff) return true
    if (this._speedLocked) return false
    if (this.gpsRedacted()) return false
    if (this.accuracy == null || this.myLat == null) return false
    return this.gpsSampleOk()
  },

  gpsRedacted() {
    if (this.myLat == null || this.myLng == null) return false
    const polys = this.pins.flatMap((pin) =>
      Array.isArray(pin.redact_polygons) ? pin.redact_polygons : []
    )
    return pointInRedactPolygons(this.myLat, this.myLng, polys)
  },

  gpsHudText() {
    if (this.gpsRedacted()) {
      const acc = this.accuracy == null ? null : Math.round(this.accuracy)
      const agl = this.altitudeUsable() ? ` · h ${this.hudHeightM().toFixed(1)}m` : ""
      return acc == null ? `Hidden here${agl}` : `GPS ±${acc}m · hidden here${agl}`
    }
    if (this.accuracy == null) return "Waiting for GPS…"
    const acc = Math.round(this.accuracy)
    const agl = this.altitudeUsable() ? ` · h ${this.hudHeightM().toFixed(1)}m` : ""
    // The fix is not moving. Say so before the accuracy copy, because a tight
    // ±3 m reading that never updates is more misleading than a coarse one
    // that does (docs/todo/20261017_qa_gps_frozen_hud.md).
    if (gpsStale({ageMs: this.fixAgeMs()})) return `GPS ±${acc}m · ${GPS_STALE_HUD}${agl}`
    if (this.gpsApproximate() || this.gpsLikelyIndoors()) {
      return `GPS ±${acc}m · no sky view${agl}`
    }
    if (this.accuracy > this.gpsTargetAccuracyM()) return `GPS ±${acc}m · calibrating${agl}`
    if (!this.gpsSampleOk()) return `GPS ±${acc}m · approximate${agl}`
    return `GPS ±${acc}m${agl}`
  },

  teardown() {
    if (this._tornDown) return
    this._tornDown = true
    this.running = false
    this._qa?.teardown()
    this.stopBtn = null
    this.cameraRecoverBtn = null
    this.startBtn?.removeEventListener("click", this.onStartClick)
    const tapEl = this.stage || this.glCanvas || this.canvas
    tapEl?.removeEventListener("pointerdown", this.onTapDown)
    tapEl?.removeEventListener("pointermove", this.onTapMove)
    tapEl?.removeEventListener("pointerup", this.onTapUp)
    window.removeEventListener("deviceorientation", this.onOrientation, true)
    window.removeEventListener("deviceorientationabsolute", this.onAbsolute, true)
    window.removeEventListener("devicemotion", this.onMotion, true)
    window.removeEventListener("compassneedscalibration", this.onCalibration)
    window.removeEventListener("resize", this.onResize)
    window.removeEventListener("orientationchange", this.onResize)
    window.removeEventListener("pageshow", this.onPageShow)
    window.removeEventListener("pagehide", this.onPageHide)
    window.removeEventListener("keydown", this._onGeoDeniedKey)
    document.removeEventListener("click", this._onGeoDeniedClick)
    document.removeEventListener("visibilitychange", this.onVisibility)
    try {
      this._geoPermStatus?.removeEventListener?.("change", this._onGeoPermChange)
    } catch {
      // PermissionsStatus may already be gone.
    }
    this._geoPermStatus = null
    if (this._absoluteTimer) clearTimeout(this._absoluteTimer)
    this.stopCameraTracks()
    this.stopGeolocation()
    if (this.modelLayer) {
      this.modelLayer.dispose()
      this.modelLayer = null
    }
    document.documentElement.classList.remove("ar-active")
    this._groundAlt = null
    this.myAlt = null
    this.altAccuracy = null
    this.poseFilter = createPoseFilter()
    this._speedLocked = false
    this.speedMps = null
  },

  async startExploring() {
    if (this._started || this.startBtn.disabled) return

    // This handler is a gesture, so unlock here. A re-entry through
    // `sessionReady()` has no gesture, so arm a first-touch unlock as well.
    CampaignAudio.unlock()
    CampaignAudio.armGestureUnlock()
    // A Start after the Settings sheet was dismissed is a new attempt, so a
    // still-denied location should reopen the sheet rather than leave the fan
    // looking at the gate with the same one-line error.
    this._geoDeniedRetry = Boolean(this._geoDeniedDismissedAt)
    this._started = true
    this._cameraReviveCount = 0
    this._cameraRepaints = 0
    this._cameraRepainting = false
    this._cameraStuck = false
    this._camLuma = null
    this.startBtn.disabled = true
    this.setStartLabel("Requesting sensors…")
    this.clearError()

    try {
      this.preloadPinVideos()
      this.modelLayer?.warmVideos()
      // Location must start in this tap. A mount prewarm (last-fix / re-entry)
      // is not a gesture: iOS 18 then never shows Allow Once / While Using /
      // Never, and this kickoff would no-op on the existing watch id.
      this.stopGeolocation()
      this.kickoffGeolocation()
      // Fire motion in the same tap. Awaiting location first lets iOS drop it.
      const motionGranted = this.requestMotionPermission()
      await this.requestOrientationPermission()
      this.bindOrientation()
      if (await motionGranted.catch(() => false)) this.bindMotion()
      // Orientation/camera prompts suspend AudioContext. Re-read the real
      // state so the chip does not claim "on" while the hymn is silent.
      CampaignAudio.syncAfterGesture()
      this.setStartLabel("Requesting camera…")
      // Open the stream, then reveal the stage, wait a layout, then play.
      // Safari can stall `play()` on a video whose box is still 0×0.
      await this.openCameraStream()
      this.enterStage()
      await this.afterLayout()
      await this.playCamera()
      this._qa?.autoStart()
      this.bindOverlayTap()
      this.startLoop()
      this.markSessionReady()
    } catch (err) {
      console.error("ARViewer init failed:", err)
      this._started = false
      this.clearSessionReady()
      this.showError(this.friendlyError(err))
      this.startBtn.disabled = false
      this.setStartLabel("Start exploring")
    }
  },

  async requestMotionPermission() {
    if (
      typeof DeviceMotionEvent !== "undefined" &&
      typeof DeviceMotionEvent.requestPermission === "function"
    ) {
      try {
        return (await DeviceMotionEvent.requestPermission()) === "granted"
      } catch {
        return false
      }
    }
    return typeof DeviceMotionEvent !== "undefined"
  },

  bindOverlayTap() {
    if (this._tapBound) return
    const el = this.stage || this.glCanvas || this.canvas
    if (!el) return
    this._tapBound = true
    this._pointer = {x: 0, y: 0, moved: false}
    this.onTapDown = (e) => {
      this._pointer = {x: e.clientX, y: e.clientY, moved: false}
    }
    this.onTapMove = (e) => {
      if (Math.hypot(e.clientX - this._pointer.x, e.clientY - this._pointer.y) > 14) {
        this._pointer.moved = true
      }
    }
    this.onTapUp = (e) => this.handleOverlayTap(e)
    el.addEventListener("pointerdown", this.onTapDown)
    el.addEventListener("pointermove", this.onTapMove)
    el.addEventListener("pointerup", this.onTapUp)
  },

  handleOverlayTap(event) {
    if (this._pointer?.moved || this._handoff || !this.gpsOverlayOk()) return
    const visible = this.nearestDiscoverable()
    if (!visible?.pin?.model_url) return
    const canvas = this.glCanvas || this.canvas
    const rect = canvas.getBoundingClientRect()
    if (rect.width < 1 || rect.height < 1) return
    const ndcX = ((event.clientX - rect.left) / rect.width) * 2 - 1
    const ndcY = -(((event.clientY - rect.top) / rect.height) * 2 - 1)
    if (!this.modelLayer?.hitTest(ndcX, ndcY)) return
    if (!tapToPlay(visible.zone, visible.pin)) return
    const pin = visible.pin
    const look = lingerSeconds(visible.zone, pin)
    const id = String(pin.id)
    this._kraxtisKind.set(id, lingerAllowed(visible.zone, pin) ? "linger" : "tap")
    this._dwell.set(id, {
      startedAt: performance.now() - look * 1000,
      lastGoodAt: performance.now(),
    })
    this.tickWorldOverlay(pin, look)
  },

  async requestOrientationPermission() {
    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function"
    ) {
      const response = await DeviceOrientationEvent.requestPermission()
      if (response !== "granted") {
        throw new Error(
          "Compass permission denied. Settings → Safari → Motion & Orientation Access."
        )
      }
    }
  },

  bindOrientation() {
    window.addEventListener("compassneedscalibration", this.onCalibration)

    if (this.hudCompass) this.hudCompass.textContent = "Compass: listening…"

    if (this._ios) {
      // iOS compass heading lives on deviceorientation, not the absolute event.
      window.addEventListener("deviceorientation", this.onOrientation, true)
      return
    }

    // Prefer absolute heading. Keep a relative fallback until one absolute event arrives.
    window.addEventListener("deviceorientationabsolute", this.onAbsolute, true)
    window.addEventListener("deviceorientation", this.onOrientation, true)
  },

  bindMotion() {
    window.addEventListener("devicemotion", this.onMotion, true)
  },

  handleMotion(e) {
    const g = e.accelerationIncludingGravity
    const rate = e.rotationRate
    if (!g || !rate) {
      this.poseFilter.setGyro(null)
      return
    }

    const gx = g.x
    const gy = g.y
    const gz = g.z
    const gLen = Math.hypot(gx, gy, gz)
    if (!(gLen > 4)) {
      this.poseFilter.setGyro(null)
      return
    }

    const wx = typeof rate.beta === "number" ? rate.beta : 0
    const wy = typeof rate.gamma === "number" ? rate.gamma : 0
    const wz = typeof rate.alpha === "number" ? rate.alpha : 0
    const degPerSec = (wx * gx + wy * gy + wz * gz) / gLen
    this.poseFilter.setGyro((degPerSec * Math.PI) / 180)
  },

  handleOrientation(e, fromAbsolute) {
    if (fromAbsolute && typeof e.alpha === "number") this._gotAbsolute = true
    if (this._gotAbsolute && !fromAbsolute && !this._ios) return

    const pose = this.cameraPoseFromEvent(e)
    if (pose) {
      const hdgAcc = this.headingAccuracyDeg(e)
      this._qa?.noteHeadingAcc(hdgAcc)
      this.poseFilter.updateHeading(pose.heading, hdgAcc)
      this.poseFilter.updatePitch(pose.pitch)
    }

    this.snapshotPose()
    this.paintHud(false)
  },

  cameraPoseFromEvent(e) {
    let alpha = typeof e.alpha === "number" ? e.alpha : null
    // W3C alpha increases CCW; Apple compass heading increases CW from north.
    if (typeof e.webkitCompassHeading === "number" && !Number.isNaN(e.webkitCompassHeading)) {
      alpha = 360 - e.webkitCompassHeading
    }
    if (alpha == null || Number.isNaN(alpha)) return null
    const beta = typeof e.beta === "number" ? e.beta : 0
    const gamma = typeof e.gamma === "number" ? e.gamma : 0
    return headingPitchFromDevice(alpha, beta, gamma, this.screenAngleDeg())
  },

  screenAngleDeg() {
    if (typeof screen !== "undefined" && screen.orientation && typeof screen.orientation.angle === "number") {
      return screen.orientation.angle
    }
    if (typeof window.orientation === "number") return window.orientation
    return 0
  },

  headingAccuracyDeg(e) {
    if (typeof e.webkitCompassAccuracy === "number" && e.webkitCompassAccuracy >= 0) {
      return Math.min(Math.max(e.webkitCompassAccuracy, 5), 60)
    }
    return 18
  },

  normalizeDegrees(deg) {
    return ((deg % 360) + 360) % 360
  },

  shortestDelta(from, to) {
    let diff = this.normalizeDegrees(to) - this.normalizeDegrees(from)
    if (diff > 180) diff -= 360
    if (diff < -180) diff += 360
    return diff
  },

  // Two steps on purpose: open the stream first, reveal the stage, then play.
  async openCameraStream() {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser cannot open the camera.")
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {facingMode: {ideal: "environment"}},
      audio: false,
    })

    this.vidStream = stream
    this.video.setAttribute("playsinline", "")
    this.video.setAttribute("webkit-playsinline", "")
    this.video.muted = true
    this.video.playsInline = true
    this.video.srcObject = stream

    for (const track of stream.getVideoTracks()) {
      // iOS suspends the capture session on background/lock. The element keeps
      // a dead `srcObject`, which renders black and raises nothing, so the
      // HUD keeps working while the feed is gone. Own the lifecycle.
      track.onended = () => this.onCameraEnded()
      track.onmute = () => this.onCameraMuted()
    }
  },

  async playCamera() {
    if (!this.vidStream) throw new Error("Camera is not open.")

    await new Promise((resolve, reject) => {
      let settled = false
      const ok = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve()
      }
      const fail = (err) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        reject(err)
      }

      const play = () => {
        const started = this.video.play()
        if (started && typeof started.then === "function") started.then(ok, fail)
        else ok()
      }

      const timer = setTimeout(
        () => fail(new Error("Camera timed out. Reload and try again.")),
        8000
      )

      if (this.video.readyState >= 2) {
        play()
        return
      }

      this.video.onloadedmetadata = play
    })
    this._cameraStartedAt = performance.now()
  },

  afterLayout() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })
  },

  cameraQaState() {
    const track = this.vidStream?.getVideoTracks?.()[0]
    const videoWidth = this.video?.videoWidth || 0
    const readyState = this.video?.readyState || 0
    const trackLive = track?.readyState === "live"
    const trackMuted = Boolean(track?.muted)
    return {
      camLive: trackLive,
      camMuted: trackMuted,
      camW: videoWidth,
      camLuma: this._camLuma,
      camOk: cameraPictureOk({
        trackLive,
        trackMuted,
        videoWidth,
        readyState,
        meanLuma: this._camLuma,
      }),
    }
  },

  cameraPictureNow() {
    const track = this.vidStream?.getVideoTracks?.()[0]
    this._camLuma = sampleVideoLuma(this.video, this._camProbe, this._camProbeCtx)
    return cameraPictureOk({
      trackLive: track?.readyState === "live",
      trackMuted: Boolean(track?.muted),
      videoWidth: this.video?.videoWidth || 0,
      readyState: this.video?.readyState || 0,
      meanLuma: this._camLuma,
    })
  },

  onCameraEnded() {
    if (this._tornDown) return
    this.setHint("Camera stopped. Tap to reconnect.")
    this.showCameraRecover()
  },

  // `MediaStreamTrack.stop()` sets readyState to "ended" *without* firing
  // `ended` (spec), and an iOS-suspended capture session can go dead without
  // any event at all. So the only reliable signal is polling the track.
  // Runs from the render loop, throttled; while the page is hidden the
  // pageshow/visibility revive path owns recovery.
  watchCamera() {
    if (!this._started || this._cameraReviving || this._tornDown) return
    const now = performance.now()
    if (now - (this._cameraCheckAt || 0) < 2000) return
    this._cameraCheckAt = now

    const tracks = this.vidStream?.getVideoTracks?.() || []
    const dead = tracks.length === 0 || tracks.every((track) => track.readyState !== "live")
    if (dead) {
      this.onCameraEnded()
      return
    }
    const pictureOk = this.cameraPictureNow()
    if (pictureOk) {
      this.clearCameraRecover()
      this._cameraRepaints = 0
      this._cameraStuck = false
      return
    }
    this.showCameraRecover()

    // A black frame with a healthy track has three documented causes and only
    // one of them is worth acting on in-page (see `camera_paint.js`). The rule
    // is ordered and bounded: one cheap `srcObject` bounce, then stop and tell
    // the fan. It deliberately never re-requests the camera by itself — the
    // 2026-10-26 report is "it keeps trying again and again", which was exactly
    // that, and it makes a wedged iOS capture session harder to recover.
    const probe = {
      trackLive: true,
      trackMuted: false,
      videoWidth: this.video?.videoWidth || 0,
      canvasLuma: this._camLuma,
      attempts: this._cameraRepaints || 0,
      repainting: this._cameraRepainting,
    }
    if (shouldRepaint(probe)) {
      this.bounceCamera()
      return
    }
    if (shouldStopRetrying(probe)) {
      // Once per stuck period: setting the hint every 2 s would keep waking the
      // HUD's live region, and re-telling the fan the same thing reads as a
      // page that is still trying.
      if (!this._cameraStuck) {
        this._cameraStuck = true
        this.setHint(recoveryMessage())
      }
      return
    }
  },

  // WebKit 310349: the element stops being composited while the track keeps
  // producing frames. Detaching and re-attaching `srcObject` forces the video
  // layer to be recreated. This is the documented workaround, and it is much
  // cheaper than re-requesting the camera.
  async bounceCamera() {
    if (this._cameraRepainting || this._tornDown || !this.vidStream) return
    this._cameraRepainting = true
    this._cameraRepaints = (this._cameraRepaints || 0) + 1
    try {
      const stream = this.vidStream
      this.video.srcObject = null
      await new Promise((resolve) => setTimeout(resolve, 120))
      if (this._tornDown) return
      this.video.srcObject = stream
      this.video.playsInline = true
      this.video.muted = true
      await this.video.play().catch(() => {})
      await this.afterLayout()
      if (this.cameraPictureNow()) {
        this.clearCameraRecover()
        this.setHint("")
        this._cameraRepaints = 0
      }
    } catch (err) {
      console.warn("camera repaint failed", err)
    } finally {
      this._cameraRepainting = false
    }
  },

  // `mute` is often transient (iOS fires it on background and pairs it with
  // `unmute`). Do not accuse the camera of being paused; give it a moment,
  // then revive only if it is still muted.
  onCameraMuted() {
    if (this._tornDown) return
    clearTimeout(this._cameraMuteTimer)
    this._cameraMuteTimer = setTimeout(() => {
      if (this._tornDown || !this._started) return
      const track = this.vidStream?.getVideoTracks?.()[0]
      if (track && track.muted) this.reviveCamera()
    }, 1500)
  },

  // Re-request after a background/lock cycle. Cheap and idempotent: it stops
  // any dead tracks first so iOS does not hand back the same suspended session.
  // `force` is a real user gesture (the recover button). The cap exists to stop
  // an automatic loop from hammering getUserMedia, not to lock the fan out: an
  // auto-revive that failed three times across background cycles used to make
  // the button a no-op until a page reload.
  //
  // Automatic revives are capped at ONE. Each one tears down the capture
  // session and asks the OS again; against a wedged session (2026-10-26, iOS 27,
  // both browsers) three requests is the "keeps trying again and again" the fan
  // sees, and it makes recovery less likely. A tap on the recover button is a
  // fresh attempt and still resets the count.
  async reviveCamera({force = false} = {}) {
    if (this._tornDown || !this._started) return
    if (this._cameraReviving) return
    if (this.cameraPictureNow()) {
      this.clearCameraRecover()
      return
    }
    if (force) {
      this._cameraReviveCount = 0
      this._cameraRepaints = 0
      this._cameraStuck = false
    }
    const budget = force ? CAMERA_MAX_REVIVES : 1
    if (this._cameraReviveCount >= budget) {
      this.showCameraRecover()
      this.setHint(recoveryMessage())
      return
    }

    this._cameraReviving = true
    this._cameraReviveCount += 1
    try {
      this.stopCameraTracks()
      await this.openCameraStream()
      await this.afterLayout()
      await this.playCamera()
      if (this.cameraPictureNow()) {
        this.clearCameraRecover()
        this.setHint("")
      } else {
        this.showCameraRecover()
      }
    } catch (err) {
      console.warn("camera revive failed", err)
      this.showCameraRecover()
    } finally {
      this._cameraReviving = false
    }
  },

  stopCameraTracks() {
    clearTimeout(this._cameraMuteTimer)
    if (!this.vidStream) return
    for (const track of this.vidStream.getTracks()) {
      track.onended = null
      track.onmute = null
      track.stop()
    }
    this.vidStream = null
    if (this.video) this.video.srcObject = null
  },

  showCameraRecover() {
    if (!this.cameraRecoverBtn) return
    this.cameraRecoverBtn.hidden = false
  },

  clearCameraRecover() {
    if (this.cameraRecoverBtn) this.cameraRecoverBtn.hidden = true
  },

  // End the session from the stage: release camera + sensors and go back to
  // the gate. The fan keeps the page, so a second tap can start again.
  stopSession() {
    this.running = false
    this._started = false
    this._handoff = null
    this._activateSent = false
    this._dwell = new Map()
    this._playStarted = new Set()
    this._kraxtisKind = new Map()
    this._qa?.teardown()
    // The loop is `requestAnimationFrame`-driven and self-stopping on
    // `running = false`; nothing else to cancel.
    this.stopCameraTracks()
    this.stopGeolocation()
    this.clearSessionReady()
    this.setChromeHidden(false)
    if (this.modelLayer) {
      this.modelLayer.pauseVideo?.()
      this.modelLayer.resetVideo?.()
    }
    // The stage keeps its last HUD/zone/seek content, and the QA panel keeps
    // its clock. None of it is meaningful with the session released, and the
    // next start renders a frame immediately — clear the cards so the gate
    // that comes up behind them is clean.
    if (this.rangeBanner) this.rangeBanner.hidden = true
    if (this.zoneChip) this.zoneChip.hidden = true
    if (this.seek) this.seek.hidden = true
    if (this.lockEl) this.lockEl.hidden = true
    if (this.hudNearest) this.hudNearest.textContent = "Finding pins…"
    if (this.stage) this.stage.hidden = true
    if (this.gate) this.gate.hidden = false
    document.documentElement.classList.remove("ar-active")
    this.clearCameraRecover()
    this.setHint("")
    this.startBtn.disabled = false
    this.setStartLabel("Start exploring")
  },

  stopGeolocation() {
    if (this._geoStop) {
      this._geoStop()
      this._geoStop = null
    } else if (usableWatchId(this.watchId) && navigator.geolocation) {
      navigator.geolocation.clearWatch(this.watchId)
    }
    this.watchId = null
  },

  geoSilentMs() {
    const last = this._lastFixAt || this._watchArmedAt || this._gpsStartedAt
    if (!last) return Infinity
    return Date.now() - last
  },

  reviveGeolocation({evenIfDenied = false, reason = "foreground", hiddenMs = 0} = {}) {
    if (this._tornDown || !this._gpsStartedAt) return
    if (
      !shouldReviveWatch({
        denied: this._geoError?.code === 1,
        evenIfDenied,
        hasWatch: usableWatchId(this.watchId),
        hiddenMs,
        armedMs: this._watchArmedAt ? Date.now() - this._watchArmedAt : Infinity,
        reason,
      })
    ) {
      return
    }
    this.stopGeolocation()
    this._gpsNeedsSeed = true
    this._liveGps = false
    this.kickoffGeolocation()
  },

  kickoffGeolocation() {
    if (this._tornDown) return
    if (!navigator.geolocation) {
      this.showError("Location is not available on this browser.")
      return
    }

    if (usableWatchId(this.watchId)) return
    this.stopGeolocation()

    if (this.accuracy == null) this.setHudGps("GPS: requesting…")
    this._gpsStartedAt = this._gpsStartedAt || Date.now()

    const apply = (pos) => {
      if (this._tornDown) return
      const lat = gpsCoord(pos.coords.latitude)
      const lng = gpsCoord(pos.coords.longitude)
      const acc = gpsAccuracyM(pos.coords.accuracy)
      if (!gpsCoordsOk(lat, lng)) return
      if (gpsIgnoreSpike(acc, this._bestAcc)) {
        this.paintHud(true)
        return
      }
      const ageMs = gpsSampleAgeMs(pos.timestamp)
      if (this._gpsNeedsSeed && !gpsSeedSampleOk(ageMs)) {
        this.paintHud(true)
        return
      }

      this._geoError = null
      this._liveGps = true
      this._lastFixAt = Date.now()
      this._fixAgeMs = ageMs
      this.accuracy = acc
      this._qa?.noteRaw(lat, lng)
      const ts = gpsPositionTimestampMs(pos.timestamp)
      if (this._gpsNeedsSeed) {
        this.poseFilter.seed(lat, lng, acc)
        this._groundAlt = null
        this._gpsNeedsSeed = false
      } else {
        this.poseFilter.updateGps(lat, lng, acc, ts)
      }
      this.snapshotPose()
      const now = Date.now()
      if (this._bestAcc == null || acc <= this._bestAcc) {
        this._bestAcc = acc
        this._bestAccAt = now
      }
      if (acc <= GPS_COARSE_MAX_M) this.writeLastFix(lat, lng, acc)

      const alt = pos.coords.altitude
      if (typeof alt === "number" && !Number.isNaN(alt)) {
        this.myAlt = alt
        this.altAccuracy =
          typeof pos.coords.altitudeAccuracy === "number" ? pos.coords.altitudeAccuracy : null
        this.noteGroundAltitude()
      }
      this.poseFilter.updateAltitude(alt, this.altAccuracy, ts)
      this.paintHud(true)
    }

    let deniedThisWatch = false
    const onWatchError = (err) => {
      if (this._tornDown) return
      this._geoError = err
      if (err?.code === 1) {
        deniedThisWatch = true
        this.stopGeolocation()
        this.showError("Location permission denied. Enable Location for Safari in Settings.")
        this.setHudGps("GPS permission denied")
        this.offerLocationSettings({retried: Boolean(this._geoDeniedRetry)})
      }
    }

    const session = startGeolocation(navigator.geolocation, {
      onFix: apply,
      onError: onWatchError,
    })
    this._geoStop = session.stop
    this.watchId = session.watchId
    if (usableWatchId(this.watchId)) this._watchArmedAt = Date.now()
    if (deniedThisWatch) this.stopGeolocation()
  },

  enterStage() {
    document.documentElement.classList.add("ar-active")
    this.gate.hidden = true
    this.stage.hidden = false
    this.ensureModelLayer()
    this.resizeCanvas()
    requestAnimationFrame(() => this.resizeCanvas())
  },

  ensureModelLayer() {
    if (this.modelLayer || !this._webgl || !this.glCanvas) return
    try {
      this.modelLayer = createModelLayer(this.glCanvas)
    } catch (err) {
      console.warn("WebGL overlay unavailable", err)
      this._webgl = false
    }
  },

  preloadPinVideos() {
    this.ensureModelLayer()
    if (!this.modelLayer) return
    const urls = this.pins
      .filter((pin) => pin.overlay_kind === "mp4" && pin.model_url)
      .map((pin) => pin.model_url)
    this.modelLayer.preloadVideos(urls)
    this.modelLayer.preloadTextures(this.pins.map((pin) => pin.mosaic_url).filter(Boolean))
  },

  startLoop() {
    this.running = true
    this.render()
  },

  resizeCanvas() {
    if (!this.canvas) return

    const rect = this.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    this.w = rect.width
    this.h = rect.height
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr))
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr))
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.modelLayer?.resize(this.w, this.h, dpr)
  },

  altitudeUsable() {
    if (typeof this.myAlt !== "number" || Number.isNaN(this.myAlt)) return false
    if (this.altAccuracy == null) return true
    return this.altAccuracy <= 20
  },

  noteGroundAltitude() {
    if (this._groundAlt != null || !this.altitudeUsable()) return
    this._groundAlt = this.myAlt
  },

  viewerAgl() {
    if (this.altitudeUsable() && this._groundAlt != null) {
      return this.myAlt - this._groundAlt
    }
    return 1.5
  },

  // Height of the viewer's eye above the venue floor. Venue-locked events use
  // the captured floor altitude so the overlay sits at a fixed world height
  // for every viewer; others keep the legacy first-sample delta. The vertical
  // Kalman's sigma goes with it: an untrusted estimate falls back to an
  // assumed eye height rather than displacing the overlay by metres.
  heightAboveFloor(pin) {
    const est = this.poseFilter.altitudeEstimate()
    return heightAboveFloorM({
      floorAltM: floorAltM(pin),
      altM: est ? est.value : null,
      sigmaM: est ? est.sigma : null,
      fallbackM: this.viewerAgl(),
    })
  },

  hudHeightM() {
    let bestD = Infinity
    let bestH = null
    for (const pin of this.pins) {
      if (floorAltM(pin) == null) continue
      const d = this.pinDistance(pin)
      if (d < bestD) {
        bestD = d
        bestH = this.heightAboveFloor(pin)
      }
    }
    return bestH != null ? bestH : this.viewerAgl()
  },

  verticalInside(pin) {
    // GPS ellipsoid altitude routinely walks several meters (and often
    // below the first sample). A 5 m pin only has a 7 m vertical band, so a
    // hard height gate false-excludes people standing on the pin. Range
    // membership is horizontal; this is diagnostics-only and only culls when
    // the estimate is actually trustworthy.
    const est = this.poseFilter.altitudeEstimate()
    if (!est || est.sigma > 8) return true

    const zone = this._geoById.get(String(pin.id))?.zone
    const top = Math.max(aglForZone(zone, pin), 0) + 2
    const height = this.heightAboveFloor(pin)
    const slop = Math.max(est.sigma, 3)
    return height >= -slop && height <= top + slop
  },

  pinDistance(pin) {
    if (this.myLat == null || this.myLng == null) return Infinity
    return haversineM(this.myLat, this.myLng, pin.lat, pin.lng)
  },

  beginFrame() {
    this._geoById = new Map()
    if (this.myLat == null || !this.gpsOverlayOk()) return
    const now = performance.now()
    for (const pin of this.pins) {
      const d = this.pinDistance(pin)
      this.stickyFor(String(pin.id)).update(d, this.accuracy, radiiFromPin(pin), now)
    }
  },

  stickyFor(id) {
    if (!this._stickyById.has(id)) this._stickyById.set(id, createStickyZone())
    return this._stickyById.get(id)
  },

  overlaySize(pin, zone, slantM) {
    return sizeMForZone(zone, pin, {
      canvasW: this.w,
      canvasH: this.h,
      fovH: AR_FOV_H,
      slantM,
      aspect: this.modelLayer?.planeAspect?.() || 1,
    })
  },

  usableAltitudeDelta(pin, zone) {
    return aglForZone(zone || "activation", pin) - this.heightAboveFloor(pin)
  },

  toENU(pin, zone) {
    const R = EARTH_M
    const latRad = (this.myLat * Math.PI) / 180
    const dLat = ((pin.lat - this.myLat) * Math.PI) / 180
    const dLng = ((pin.lng - this.myLng) * Math.PI) / 180
    return {
      east: R * Math.cos(latRad) * dLng,
      north: R * dLat,
      up: this.usableAltitudeDelta(pin, zone),
    }
  },

  closeRangeUnstable(horiz, up, sizeM) {
    const acc = Number.isFinite(this.accuracy) ? this.accuracy : 12
    const size = Number(sizeM) > 0 ? Number(sizeM) : 1.5
    const inside = horiz < Math.max(6, size * 0.65, Math.abs(up) * 1.15)
    const gpsCantAim = horiz < 20 && acc / Math.max(horiz, 1) > 0.45
    return inside || gpsCantAim
  },

  stabilizeEnu(pin, enu, zone) {
    if (zone !== "activation") return enu
    const id = String(pin.id)
    const horiz = Math.hypot(enu.east, enu.north)
    const size =
      Number(pin.apparent_size_activation) > 0
        ? Number(pin.apparent_size_activation)
        : DEFAULT_SIZE_ACTIVATION
    const minH = Math.max(4, size * 0.6)

    if (!this.closeRangeUnstable(horiz, enu.up, size)) {
      this._closeLock.delete(id)
      return enu
    }

    let lock = this._closeLock.get(id)
    if (!lock) {
      if (horiz > 1.2) {
        const scale = minH / horiz
        lock = {east: enu.east * scale, north: enu.north * scale}
      } else if (this.heading != null) {
        const yaw = (this.heading * Math.PI) / 180
        lock = {east: Math.sin(yaw) * minH, north: Math.cos(yaw) * minH}
      } else {
        return enu
      }
      this._closeLock.set(id, lock)
    }

    return {east: lock.east, north: lock.north, up: enu.up}
  },

  pinGeometry(pin) {
    const id = String(pin.id)
    const cached = this._geoById.get(id)
    if (cached) return cached

    const d = this.pinDistance(pin)
    const zone = this.stickyFor(id).peek()
    const raw = this.toENU(pin, zone)
    let enu = this.stabilizeEnu(pin, raw, zone)
    if (zone === "remote" && this.myLat != null) {
      const bearing = initialBearingDeg(this.myLat, this.myLng, pin.lat, pin.lng)
      const rig = skyRigEnu(bearing, aglForZone(zone, pin), this.heightAboveFloor(pin), d)
      enu = {east: rig.east, north: rig.north, up: rig.up}
    }
    const horiz = Math.hypot(enu.east, enu.north)
    const bearing = this.normalizeDegrees((Math.atan2(enu.east, enu.north) * 180) / Math.PI)
    const rel = this.heading == null ? 0 : this.shortestDelta(this.heading, bearing)
    const elev = (Math.atan2(enu.up, Math.max(horiz, 1)) * 180) / Math.PI
    const elevRel = elev - (this.pitch || 0)
    const dist = Math.hypot(horiz, enu.up)
    const geo = {
      enu,
      horiz,
      rawHoriz: d,
      bearing,
      rel,
      elev,
      elevRel,
      dist,
      zone,
      sizeM: this.overlaySize(pin, zone, dist),
      sizeMode: sizeModeForZone(zone, pin.overlay_kind),
    }
    this._geoById.set(id, geo)
    return geo
  },

  // Activation/detection accept by the poster's **extent**, not its origin.
  //
  // The origin test asked the fan to centre the pin, but in activation the
  // overlay is `apparent_size_activation` metres on its longest edge: 20 m at
  // 14 m is a 62 deg poster in a 54 deg vertical half-FOV. The 09:51 field walk
  // held the pin's origin 3 deg above the top edge for 33 s while the poster
  // reached 28 deg into the frame (docs/reviews/20261021_qa_corpus_analysis.md
  // §3). `posterSpansView` accepts that; the pure geometry lives in ar_look.js.
  //
  // Beacon/remote keep the origin test: the overlay there is sized to a
  // viewport fraction, so finding it on the horizon is the UX.
  lookingAtPin(pin, geo) {
    if (this.projectPin(pin)) return true

    if (geo.zone === "activation" || geo.zone === "detection") {
      const ndc = pinNdc({
        heading: this.heading,
        pitch: this.pitch,
        w: this.w,
        h: this.h,
        enu: geo.enu,
        fovH: AR_FOV_H,
      })
      if (ndc) {
        const half = posterHalfNdc({
          sizeM: geo.sizeM,
          planeAspect: this.modelLayer?.planeAspect?.() || 1,
          dist: ndc.dist,
          w: this.w,
          h: this.h,
          fovH: AR_FOV_H,
        })
        if (posterSpansView({ndc, ...half})) return true
      }
    }

    if (!this.closeRangeUnstable(geo.rawHoriz, geo.enu.up, geo.sizeM)) return false
    return Math.abs(geo.elevRel) < 28
  },

  projectPin(pin) {
    if (this.heading == null || this.myLat == null) return null

    const {enu} = this.pinGeometry(pin)
    const yaw = (this.heading * Math.PI) / 180
    const pitch = ((this.pitch || 0) * Math.PI) / 180

    const fwdE = Math.sin(yaw) * Math.cos(pitch)
    const fwdN = Math.cos(yaw) * Math.cos(pitch)
    const fwdU = Math.sin(pitch)

    const rightE = Math.cos(yaw)
    const rightN = -Math.sin(yaw)
    const rightU = 0

    const upE = rightN * fwdU - rightU * fwdN
    const upN = rightU * fwdE - rightE * fwdU
    const upU = rightE * fwdN - rightN * fwdE

    const forward = enu.east * fwdE + enu.north * fwdN + enu.up * fwdU
    const right = enu.east * rightE + enu.north * rightN + enu.up * rightU
    const camUp = enu.east * upE + enu.north * upN + enu.up * upU

    if (forward < 0.2) return null

    const fovH = AR_FOV_H
    const aspect = this.w / Math.max(this.h, 1)
    const fovV = 2 * Math.atan(Math.tan(fovH / 2) / aspect)
    const ndcX = right / forward / Math.tan(fovH / 2)
    const ndcY = -(camUp / forward) / Math.tan(fovV / 2)

    if (Math.abs(ndcX) > 1.05 || Math.abs(ndcY) > 1.05) return null

    const dist = Math.hypot(enu.east, enu.north, enu.up)
    const scale = Math.max(0.35, Math.min(2.2, 40 / Math.max(dist, 1)))

    return {
      x: (this.w / 2) * (1 + ndcX),
      y: (this.h / 2) * (1 + ndcY),
      radius: 28 * scale,
      dist,
      name: pinTitle(pin),
    }
  },

  pickGlbPin() {
    if (!this.modelLayer || this.myLat == null || this.heading == null) return null

    if (this._handoff) {
      const pin = this.pins.find((item) => String(item.id) === this._handoff.pinId)
      if (pin?.model_url) return {pin, ...this.pinGeometry(pin)}
    }

    if (this.exclusionPin()) return null

    let best = null
    for (const pin of this.pins) {
      if (!pin.model_url) continue
      const geo = this.pinGeometry(pin)
      if (!overlayVisible(geo.zone)) continue
      if (!best || geo.rawHoriz < best.rawHoriz) best = {pin, ...geo}
    }
    return best
  },

  syncGlb() {
    if (!this.modelLayer) {
      this._glbPinId = null
      return
    }

    const picked = this.pickGlbPin()
    if (!picked || !this.gpsOverlayOk()) {
      this.modelLayer.load(null)
      this._glbPinId = null
      this.modelLayer.render()
      return
    }

    this.modelLayer.setCamera(this.heading, this.pitch)
    this.modelLayer.setContainFill(picked.pin.handoff_contain_fraction)
    const handingOff =
      this._handoff && String(picked.pin.id) === this._handoff.pinId
    const holdOverlay =
      handingOff && ["fade", "hold", "cover"].includes(this._handoff.phase)
    if (!holdOverlay) {
      this.modelLayer.load(picked.pin.model_url, picked.pin.overlay_kind)
    }

    if (handingOff) {
      if (!this._handoff.snapshot && this.modelLayer.activeUrl()) {
        this.modelLayer.pose(
          picked.enu,
          facingForZone(picked.zone, picked.pin.model_facing),
          picked.pin.model_yaw_deg,
          picked.sizeM,
          picked.sizeMode
        )
        this._handoff.snapshot = this.modelLayer.snapshotPose()
        this._handoff.startedAt = performance.now()
        this._handoff.phase = "fly"
      }
      const handoff = this._handoff
      const flyMs = Math.max(handoff.flyMs, 1)
      if (handoff.phase === "cover" && handoff.coverFrom) {
        const t = (performance.now() - handoff.coverStartedAt) / flyMs
        this.modelLayer.applyApproach(handoff.coverFrom, t, "cover")
      } else if (handoff.snapshot) {
        const t = handoff.phase === "fly" ? (performance.now() - handoff.startedAt) / flyMs : 1
        this.modelLayer.applyApproach(handoff.snapshot, t, "contain")
      }
    } else {
      this.modelLayer.pose(
        picked.enu,
        facingForZone(picked.zone, picked.pin.model_facing),
        picked.pin.model_yaw_deg,
        picked.sizeM,
        picked.sizeMode
      )
    }
    this.modelLayer.render()
    this._glbPinId =
      this.modelLayer.activeUrl() === picked.pin.model_url ? String(picked.pin.id) : null
  },

  lingerStickyId() {
    if (this._handoff?.pinId) return String(this._handoff.pinId)

    const kinds = this._kraxtisKind
    const dwell = this._dwell
    if (!kinds || typeof kinds[Symbol.iterator] !== "function") return null

    for (const [id, kind] of kinds) {
      if ((kind === "linger" || kind === "tap") && dwell?.has?.(id)) return id
    }

    return null
  },

  nearestPin() {
    const pins = Array.isArray(this.pins) ? this.pins : []
    const stickyId = this.lingerStickyId()
    if (stickyId) {
      const pin = pins.find((item) => String(item.id) === stickyId)
      if (pin) {
        if (this.myLat == null) return {pin, rawHoriz: 0, zone: null, enu: {east: 0, north: 0, up: 0}}
        return {pin, ...this.pinGeometry(pin)}
      }
    }

    if (this.myLat == null) return pins[0] ? {pin: pins[0], rawHoriz: 0, zone: null, enu: {east: 0, north: 0, up: 0}} : null

    let best = null
    for (const pin of pins) {
      const geo = this.pinGeometry(pin)
      if (!best || geo.rawHoriz < best.rawHoriz) best = {pin, ...geo}
    }
    return best
  },

  // Exclusion is the ground under the fan, not one pin's artwork. A campaign
  // draws one hologram, so standing inside *any* enabled pin's exclusion
  // cylinder hides the show for the whole event: the nearest other pin must
  // not keep drawing while the "Too close" card is up.
  exclusionPin() {
    if (this.myLat == null) return null
    const geos = this.pins.map((pin) => ({pin, ...this.pinGeometry(pin)}))
    return exclusionOwner(geos)
  },

  nearestDiscoverable() {
    if (this.myLat == null || this.exclusionPin()) return null

    let best = null
    for (const pin of this.pins) {
      const geo = this.pinGeometry(pin)
      if (!overlayVisible(geo.zone)) continue
      if (!best || geo.rawHoriz < best.rawHoriz) best = {pin, ...geo}
    }
    return best
  },

  reportLooking(pinId) {
    if (pinId === this._lookingAt) return
    this._lookingAt = pinId
    try {
      this.pushEvent("looking", {pin_id: pinId || ""})
    } catch (err) {
      console.warn("looking event dropped", err)
    }
  },

  setHudGps(text) {
    if (this.hudGps) this.hudGps.textContent = text
  },

  paintHud(force) {
    if (this.running) {
      const nearest = this.nearestPin()
      const visible = this.nearestDiscoverable()
      const projected = visible ? this.projectPin(visible.pin) : null
      this.updateHud(nearest, visible, projected, this.diagHeldSeconds(performance.now()), force)
      return
    }
    this.beginFrame()
    const nearest = this.nearestPin()
    const visible = this.nearestDiscoverable()
    const projected = visible ? this.projectPin(visible.pin) : null
    const held = this.tickDwells()
    this.updateHud(nearest, visible, projected, held, force)
  },

  updateHud(nearest, visible, projected, held, force) {
    this.updateZoneChip(nearest)
    const now = performance.now()
    if (!force && now - this._lastHudAt < 180) return
    this._lastHudAt = now
    this._qa?.paint()
    if (this._handoff) {
      if (this.seek) this.seek.hidden = true
      if (this.rangeBanner) this.rangeBanner.hidden = true
      if (this.hudDiag) this.hudDiag.hidden = true
      this.setHint("")
      if (this._speedLocked) this.updateLockCard()
      return
    }

    if (this.hudCompass) {
      this.hudCompass.textContent =
        this.heading == null ? "Compass…" : `Facing ${Math.round(this.heading)}°`
    }

    if (this.hudGps) {
      if (this._geoError && this._geoError.code === 1) {
        this.hudGps.textContent = "GPS permission denied"
        this.hudGps.classList.add("ar-hud-warn")
      } else {
        this.hudGps.textContent = this.gpsHudText()
        this.hudGps.classList.toggle(
          "ar-hud-warn",
          this.accuracy == null ||
            this.accuracy > this.gpsTargetAccuracyM() ||
            gpsStale({ageMs: this.fixAgeMs()})
        )
      }
    }

    if (this.hudNearest) {
      const exclusion = this.exclusionPin()
      if (!this.pins.length) {
        this.hudNearest.textContent = "No pins"
      } else if (!this.gpsOverlayOk()) {
        this.hudNearest.textContent = this._speedLocked
          ? SPEED_WARN
          : this.gpsRedacted()
            ? REDACT_WARN
            : this.gpsLikelyIndoors()
              ? SKY_WAIT_NEAR
              : "Holding the hologram until GPS settles"
      } else if (exclusion) {
        this.hudNearest.textContent = `${pinTitle(exclusion.pin)} · ${Math.round(exclusion.rawHoriz)}m · too close`
      } else if (!nearest) {
        this.hudNearest.textContent = "Waiting for GPS…"
      } else if (visible) {
        this.hudNearest.textContent = `${pinTitle(visible.pin)} · ${Math.round(visible.rawHoriz)}m`
      } else {
        this.hudNearest.textContent = `${pinTitle(nearest.pin)} · ${Math.round(nearest.rawHoriz)}m · out of range`
      }
    }

    if (this.rangeBanner && this.rangeText) {
      this.rangeBanner.hidden = !visible
      if (visible) {
        this.rangeText.textContent = `${pinTitle(visible.pin)} — ${visible.zone} · ${Math.round(visible.rawHoriz)}m`
      }
    }

    this.updateLockCard()
    this.updateDiagnostics(nearest, visible, projected, held)

    if (!this.gpsOverlayOk()) {
      if (this.seek) this.seek.hidden = true
      if (this.rangeBanner) this.rangeBanner.hidden = true
      this.setHint("")
      return
    }

    const zone = visible?.zone
    const d = visible?.rawHoriz ?? nearest?.rawHoriz
    if (!seekAllowed(zone, d)) {
      if (this.seek) this.seek.hidden = true
    } else {
      this.updateSeek(visible, projected)
    }

    const skyHint = gpsSkyHint({
      overlayOk: true,
      approximate: this.gpsApproximate(),
    })
    if (visible && tapHintZone(zone, visible.pin) && !this._handoff) {
      this.setHint("Tap to watch")
    } else if (skyHint) {
      this.setHint(skyHint)
    } else if (this.heading == null) {
      this.setHint("Point the camera around you. If the compass stays quiet, draw a figure-8.")
    } else {
      this.setHint("")
    }
  },

  setLockSpinning(spin) {
    if (this.lockEl) this.lockEl.classList.toggle("ar-lock-move", !spin)
    const ring = this.lockEl?.querySelector("#ar-lock-ring") || this.lockEl?.querySelector(".ar-lock-ring")
    if (ring) ring.hidden = !spin
  },

  updateLockCard() {
    if (!this.lockEl) return
    if (this.speedWarnEl) this.speedWarnEl.hidden = !this._speedLocked

    if (this._handoff) {
      this.lockEl.hidden = true
      return
    }

    if (!this.gpsOverlayOk()) {
      this.lockEl.hidden = false
      this.setLockSpinning(true)
      const acc = this.accuracy == null ? null : Math.round(this.accuracy)
      const flavor = this.lockFlavorTitle()
      if (this._speedLocked) {
        if (this.lockKicker) this.lockKicker.textContent = "Speed"
        if (this.lockTitle) this.lockTitle.textContent = "You're going too fast"
        if (this.lockBody) this.lockBody.textContent = SPEED_WARN
        this.setLockSpinning(false)
        return
      }
      if (this.gpsRedacted()) {
        if (this.lockKicker) this.lockKicker.textContent = "Hidden"
        if (this.lockTitle) this.lockTitle.textContent = "Not from here"
        if (this.lockBody) this.lockBody.textContent = REDACT_WARN
        this.setLockSpinning(false)
        return
      }
      if (this.gpsLikelyIndoors()) {
        if (this.lockKicker) this.lockKicker.textContent = "No sky view"
        if (this.lockTitle) this.lockTitle.textContent = flavor || "Waiting for satellites"
        if (this.lockBody) {
          this.lockBody.textContent = acc
            ? `GPS is ±${acc} m and not tightening. If it stays this coarse we will use it as an approximate place in a few seconds.`
            : "No satellite lock yet. A coarse network fix is enough to look around."
        }
        return
      }

      if (this.lockKicker) this.lockKicker.textContent = "Location"
      if (this.lockTitle) this.lockTitle.textContent = flavor || "Finding your place…"
      if (this.lockBody) {
        this.lockBody.textContent = acc
          ? `±${acc} m · waiting for a tighter lock, or an approximate one in a few seconds.`
          : this.gpsElapsedMs() >= GPS_COARSE_WAIT_MS
            ? "The phone still has no place. Stay on this page — a coarse fix can arrive late under a roof."
            : "Waiting for a location. The hologram stays hidden so it doesn’t point the wrong way."
      }
      return
    }

    const nearest = this.nearestPin()
    const exclusion = this.exclusionPin()
    const zone = exclusion?.zone || nearest?.zone
    const d = exclusion?.rawHoriz ?? nearest?.rawHoriz
    if (zone === "exclusion") {
      const remain = Math.max(0, Math.round((exclusion.pin.exclusion_radius_m || 0) - d))
      this.lockEl.hidden = false
      this.setLockSpinning(false)
      if (this.seek) this.seek.hidden = true
      if (this.rangeBanner) this.rangeBanner.hidden = true
      if (this.lockKicker) this.lockKicker.textContent = "Too close"
      if (this.lockTitle) this.lockTitle.textContent = "Step back to see it"
      if (this.lockBody) {
        this.lockBody.textContent = `Move about ${remain} m back. Right on top of it the hologram cannot sit still.`
      }
      return
    }
    if (zone === "beyond") {
      const cap = Number(nearest.pin.remote_radius_m) || 0
      const remain = Math.max(0, Math.round(d - cap))
      this.lockEl.hidden = false
      this.setLockSpinning(false)
      if (this.seek) this.seek.hidden = true
      if (this.rangeBanner) this.rangeBanner.hidden = true
      if (this.lockKicker) this.lockKicker.textContent = "Too far"
      if (this.lockTitle) this.lockTitle.textContent = "This event is out of range"
      if (this.lockBody) {
        this.lockBody.textContent = `Come about ${remain} m closer to enter the remote ring.`
      }
      return
    }

    this.lockEl.hidden = true
  },

  lockFlavorLines() {
    const nearest = this.nearestPin()
    if (nearest?.pin.lock_messages?.length) return nearest.pin.lock_messages
    const withLines = this.pins.filter((pin) => pin.lock_messages?.length)
    if (withLines.length === 1) return withLines[0].lock_messages
    return []
  },

  lockFlavorTitle() {
    const lines = this.lockFlavorLines()
    if (!lines.length) return null
    const tick = Math.floor(Date.now() / 2800)
    return lines[tick % lines.length]
  },

  tickDwells() {
    const now = performance.now()
    if (this.myLat == null) return 0

    if (this.gpsRedacted() && !this._handoff) {
      return this.diagHeldSeconds(now)
    }

    if (this._handoff) {
      this.tickHandoff(now)
      return this.diagHeldSeconds(now)
    }

    if (!this.gpsOverlayOk()) return this.diagHeldSeconds(now)

    // Standing inside any pin's exclusion hides the whole campaign, so no
    // other pin may keep dwelling (or play) while the "Too close" card is up.
    // The pruning pass below still runs, so a live dwell dies here.
    const excluded = Boolean(this.exclusionPin())
    const good = new Set()

    for (const pin of this.pins) {
      if (excluded) break
      const geo = this.pinGeometry(pin)
      const id = String(pin.id)
      const lingering = lingerAllowed(geo.zone, pin)
      const looking = lingering && this.lookingAtPin(pin, geo)
      const kind = this._kraxtisKind.get(id)
      const running = this._playStarted.has(id) || kind === "tap" || kind === "linger"

      if (running && kind === "tap" && overlayVisible(geo.zone)) {
        good.add(id)
        const dwell = this._dwell.get(id)
        if (dwell) dwell.lastGoodAt = now
        this.tickWorldOverlay(pin, lingerSeconds(geo.zone, pin))
        continue
      }

      if (!lingering || !looking) continue

      good.add(id)
      let state = this._dwell.get(id)
      if (!state) {
        state = {startedAt: now, lastGoodAt: now}
        this._dwell.set(id, state)
        if (!kind) this._kraxtisKind.set(id, "linger")
      } else {
        state.lastGoodAt = now
      }

      this.tickWorldOverlay(pin, (now - state.startedAt) / 1000)
    }

    for (const [id, state] of this._dwell) {
      if (good.has(id)) continue
      if (now - state.lastGoodAt > this.dwellGraceMs) {
        this._dwell.delete(id)
        this._kraxtisKind.delete(id)
        this._playStarted.delete(id)
        if (this._handoff) continue
        if (this.modelLayer) {
          this.modelLayer.pauseVideo()
          this.modelLayer.resetVideo()
        }
      }
    }

    return this.diagHeldSeconds(now)
  },

  tickWorldOverlay(pin, held) {
    const id = String(pin.id)
    const zone = this.pinGeometry(pin).zone
    const look = lingerSeconds(zone, pin)

    if (pin.overlay_kind === "mp4") {
      if (this._playStarted.has(id)) {
        const state = this.modelLayer?.videoState() || {}
        if (!state.ended) this.modelLayer?.playVideo()
        const played = this.modelLayer?.videoTime() || 0
        if (state.ended || played >= pin.play_before_handoff_seconds) this.beginHandoff(pin)
        return
      }
      if (held >= look) {
        this._playStarted.add(id)
        this.modelLayer?.playVideo()
      }
      return
    }

    if (held >= look) this.beginHandoff(pin)
  },

  abortHandoff() {
    if (!this._handoff) return
    this._handoff = null
    this._dwell = new Map()
    this._playStarted = new Set()
    this._kraxtisKind = new Map()
    this.setChromeHidden(false)
    if (this.modelLayer) {
      this.modelLayer.pauseVideo()
      this.modelLayer.resetVideo()
    }
  },

  beginHandoff(pin) {
    if (this._handoff || this._activateSent || this.gpsRedacted()) return
    this._handoff = {
      pinId: String(pin.id),
      kind: pin.overlay_kind,
      mosaicUrl: pin.mosaic_url,
      firstUrl: pin.model_url,
      phase: "fly",
      startedAt: performance.now(),
      flyMs: pin.handoff_fly_seconds * 1000,
      holdMs: pin.handoff_hold_seconds * 1000,
      // Cap for the "watch" phase. A refused or stalled play() never reports
      // `ended`, and the view then froze at the fly pose indefinitely.
      watchMs: this.watchCapMs(pin),
      watchStartedAt: null,
      snapshot: null,
      holdStartedAt: null,
      coverFrom: null,
      coverStartedAt: null,
    }
    this.setChromeHidden(true)
    this._qa?.noteHandoff()
  },

  setChromeHidden(hidden) {
    this.pageEl?.classList.toggle("ar-chrome-hidden", Boolean(hidden))
  },

  tickHandoff(now) {
    const handoff = this._handoff
    if (!handoff || this._activateSent) return
    // Once the cinematic has started, finish it. Redact/speed freeze the
    // live overlay; they must not cancel mosaic after linger.

    if (handoff.kind === "mp4" && (handoff.phase === "fly" || handoff.phase === "watch")) {
      if (!this.modelLayer?.videoState()?.ended) this.modelLayer?.playVideo()
    }

    if (!handoff.snapshot) return

    if (handoff.phase === "fly") {
      if (now - handoff.startedAt >= handoff.flyMs) {
        if (handoff.kind === "mp4" && !this.modelLayer?.videoState()?.ended) {
          handoff.phase = "watch"
          handoff.watchStartedAt = now
          // Re-derive now that metadata has had the whole fly to arrive.
          const pin = this.pins.find((item) => String(item.id) === handoff.pinId)
          if (pin) handoff.watchMs = this.watchCapMs(pin)
        } else {
          this.beginMosaic(handoff)
        }
      }
      return
    }

    if (handoff.phase === "watch") {
      // A stalled or failed clip never reports `ended`. Cap the wait at the
      // clip's own duration (or the pin's play window) plus a margin, and
      // treat a media error as finished. Without this the view sits at the
      // fly pose forever, which reads as "frozen at the edge, no video".
      const state = this.modelLayer?.videoState() || {}
      const waited = now - (handoff.watchStartedAt || handoff.startedAt)
      if (state.ended || state.error || waited >= handoff.watchMs) this.beginMosaic(handoff)
      return
    }

    if (handoff.phase === "fade") {
      if (now - (handoff.fadeStartedAt || handoff.startedAt) > 4000) {
        handoff.phase = "hold"
        handoff.holdStartedAt = now
      }
      return
    }

    if (handoff.phase === "hold") {
      if (now - handoff.holdStartedAt >= handoff.holdMs) {
        handoff.phase = "cover"
        handoff.coverFrom = this.modelLayer?.snapshotPose()
        handoff.coverStartedAt = now
      }
      return
    }

    if (handoff.phase === "cover" && now - handoff.coverStartedAt >= handoff.flyMs) {
      this._activateSent = true
      // The cover frame is the same still the mosaic opens with. Hand its URL
      // to the next page so the seam carries the image instead of a blank.
      rememberSeam(window.sessionStorage, handoff.mosaicUrl)
      this.fireActivate(handoff.pinId)
    }
  },

  // How long the "watch" phase may last. The clip's own duration is the honest
  // bound, but on a slow link the metadata may not have arrived yet — and
  // falling back to the pin's short play window would cut the campaign film in
  // half (the runner clip is 12.5 s; the play window is 2–3 s). So: duration
  // when known, otherwise a generous fixed ceiling that still bounds a stall.
  watchCapMs(pin) {
    const durationMs = Math.round(Number(this.modelLayer?.videoState()?.duration || 0) * 1000)
    if (durationMs > 0) return durationMs + 4000

    const playMs = Math.round((Number(pin.play_before_handoff_seconds) || 0) * 1000)
    return Math.max(playMs + 12_000, 20_000)
  },

  beginMosaic(handoff) {
    const same = !handoff.mosaicUrl || handoff.mosaicUrl === handoff.firstUrl
    if (same) {
      handoff.phase = "hold"
      handoff.holdStartedAt = performance.now()
      return
    }
    handoff.phase = "fade"
    handoff.fadeStartedAt = performance.now()
    this.modelLayer?.crossfadeTo(handoff.mosaicUrl, 480, () => {
      if (this._handoff !== handoff) return
      handoff.phase = "hold"
      handoff.holdStartedAt = performance.now()
    })
  },

  fireActivate(pinId) {
    const pushed = this.pushEvent("activate", {pin_id: String(pinId)})
    if (pushed && typeof pushed.catch === "function") {
      pushed.catch(() => {
        this._activateSent = false
      })
    }
  },

  diagHeldSeconds(now) {
    const visible = this.nearestDiscoverable()
    if (!visible) return 0
    const state = this._dwell.get(String(visible.pin.id))
    if (!state) return 0
    return (now - state.startedAt) / 1000
  },

  updateZoneChip(geo) {
    const el = this.zoneChip
    if (!el) return
    const zone = geo?.zone
    if (!this.diagnostics || !zone || this.myLat == null) {
      el.hidden = true
      return
    }
    el.hidden = false
    el.dataset.zone = zone
    el.textContent = zone
  },

  updateDiagnostics(nearest, visible, projected, held) {
    if (!this.hudDiag) return
    if (!this.diagnostics || !nearest) {
      this.hudDiag.hidden = true
      return
    }

    this.hudDiag.hidden = false

    if (!this.verticalInside(nearest.pin)) {
      this.hudDiag.textContent = "outside height band"
      return
    }

    const look = visible ? lingerSeconds(visible.zone, visible.pin) : this.lookSeconds
    const linger = visible && lingerAllowed(visible.zone, visible.pin)
    this.hudDiag.textContent = linger
      ? `${visible.zone} · hold ${held.toFixed(1)}s / ${look}s`
      : `${nearest.zone || "?"} · ${Math.round(nearest.rawHoriz)} m`
  },

  updateSeek(nearest, projected) {
    if (!this.seek || !this.seekArrow || !this.seekLabel) return
    if (!nearest || this.heading == null) {
      this.seek.hidden = true
      this.reportLooking(null)
      return
    }

    const onScreen = Boolean(projected)
    this.reportLooking(onScreen ? String(nearest.pin.id) : null)

    const {rel, elevRel, rawHoriz} = nearest
    const absRel = Math.abs(rel)
    const turn =
      absRel < 18 ? "ahead" : absRel > 150 ? "behind you" : rel < 0 ? "to your left" : "to your right"

    let height = ""
    if (Math.abs(nearest.enu.up) >= 2) {
      const meters = Math.round(Math.abs(nearest.enu.up))
      height = nearest.enu.up > 0 ? ` · ${meters}m above` : ` · ${meters}m below`
    } else if (Math.abs(elevRel) > 12) {
      height = elevRel > 0 ? " · look up" : " · look down"
    }

    this.seek.hidden = false
    this.seekLabel.textContent = `${pinTitle(nearest.pin)} · ${Math.round(rawHoriz)}m · ${turn}${height}`

    const yawRad = (rel * Math.PI) / 180
    const pitchRad = (elevRel * Math.PI) / 180
    let dx = Math.sin(yawRad)
    let dy = -Math.sin(pitchRad)
    const mag = Math.hypot(dx, dy) || 1
    dx /= mag
    dy /= mag

    const pad = 56
    const cx = this.w / 2
    const cy = this.h / 2
    const maxX = Math.max(cx - pad, 24)
    const maxY = Math.max(cy - pad, 24)
    const scale = Math.min(maxX / Math.max(Math.abs(dx), 0.001), maxY / Math.max(Math.abs(dy), 0.001))
    const x = cx + dx * scale * 0.78
    const y = cy + dy * scale * 0.62
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90

    this.seek.style.left = `${x}px`
    this.seek.style.top = `${y}px`
    this.seekArrow.style.transform = `rotate(${angle}deg)`
    this.seek.classList.toggle("ar-seek-onscreen", onScreen)
  },

  setHint(text) {
    if (!this.hudHint) return
    this.hudHint.textContent = text
    this.hudHint.hidden = !text
  },

  snapshotPose() {
    const snap = this.poseFilter.snapshot()
    if (!snap) return
    this.myLat = snap.lat
    this.myLng = snap.lng
    this.speedMps = snap.speedMps
    this._speedLocked = nextSpeedLocked(snap.speedMps, this._speedLocked)
    if (snap.heading != null) this.heading = snap.heading
    this.pitch = snap.pitch
  },

  render() {
    if (!this.running) return

    this.poseFilter.predict()
    this.snapshotPose()
    this.watchCamera()
    this.beginFrame()
    this.ctx.clearRect(0, 0, this.w, this.h)
    const nearest = this.nearestPin()
    const visible = this.nearestDiscoverable()
    const projected = visible ? this.projectPin(visible.pin) : null
    const overlayOk = this.gpsOverlayOk()
    if (!this._handoff && overlayOk) this.drawRadar(visible)
    this.syncGlb()
    if (!this._handoff && overlayOk) this.drawPins()
    const held = this.tickDwells()
    this.updateHud(nearest, visible, projected, held)
    requestAnimationFrame(() => this.render())
  },

  drawRadar(nearest) {
    const size = Math.min(96, this.w * 0.28)
    const cx = this.w - 16 - size / 2
    const cy = 16 + size / 2
    const r = size / 2 - 6

    this.ctx.save()
    this.ctx.beginPath()
    this.ctx.arc(cx, cy, r, 0, Math.PI * 2)
    this.ctx.fillStyle = "rgba(2, 6, 23, 0.45)"
    this.ctx.fill()
    this.ctx.strokeStyle = "rgba(226, 232, 255, 0.2)"
    this.ctx.lineWidth = 1
    this.ctx.stroke()

    // Camera always faces the top of this dial. The pin moves around you.
    this.ctx.beginPath()
    this.ctx.moveTo(cx, cy)
    this.ctx.arc(cx, cy, r * 0.92, (-112 * Math.PI) / 180, (-68 * Math.PI) / 180)
    this.ctx.closePath()
    this.ctx.fillStyle = "rgba(125, 211, 252, 0.22)"
    this.ctx.fill()

    this.ctx.fillStyle = "rgba(226, 232, 255, 0.55)"
    this.ctx.font = "9px ui-sans-serif, system-ui"
    this.ctx.textAlign = "center"
    this.ctx.fillText("view", cx, cy - r + 12)

    if (nearest && this.heading != null) {
      const rad = ((nearest.rel - 90) * Math.PI) / 180
      const px = cx + Math.cos(rad) * r * 0.72
      const py = cy + Math.sin(rad) * r * 0.72
      this.ctx.beginPath()
      this.ctx.arc(px, py, 4, 0, Math.PI * 2)
      this.ctx.fillStyle = "rgba(125, 211, 252, 0.95)"
      this.ctx.fill()
    }

    this.ctx.beginPath()
    this.ctx.arc(cx, cy, 2.5, 0, Math.PI * 2)
    this.ctx.fillStyle = "rgba(248, 250, 252, 0.9)"
    this.ctx.fill()
    this.ctx.restore()
  },

  drawPins() {
    if (this.myLat == null) return
    if (this.exclusionPin()) return

    for (const pin of this.pins) {
      const geo = this.pinGeometry(pin)
      if (!overlayVisible(geo.zone)) continue
      if (pin.model_url || String(pin.id) === this._glbPinId) continue

      const projected = this.projectPin(pin)
      if (!projected) continue

      const {x, y, radius, name} = projected
      const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, radius * 1.7)
      gradient.addColorStop(0, "rgba(186, 230, 253, 0.55)")
      gradient.addColorStop(0.55, "rgba(125, 211, 252, 0.18)")
      gradient.addColorStop(1, "rgba(125, 211, 252, 0)")

      this.ctx.beginPath()
      this.ctx.arc(x, y, radius * 1.7, 0, Math.PI * 2)
      this.ctx.fillStyle = gradient
      this.ctx.fill()

      this.ctx.beginPath()
      this.ctx.arc(x, y, radius, 0, Math.PI * 2)
      this.ctx.fillStyle = "rgba(14, 165, 233, 0.88)"
      this.ctx.fill()
      this.ctx.strokeStyle = "rgba(255, 255, 255, 0.92)"
      this.ctx.lineWidth = 2
      this.ctx.stroke()

      this.ctx.fillStyle = "rgba(255, 255, 255, 0.96)"
      this.ctx.font = `bold ${Math.round(radius * 1.05)}px ui-sans-serif, system-ui`
      this.ctx.textAlign = "center"
      this.ctx.textBaseline = "middle"
      this.ctx.fillText("✦", x, y + 1)

      this.ctx.font = "bold 13px ui-sans-serif, system-ui"
      this.ctx.textBaseline = "bottom"
      this.ctx.lineWidth = 4
      this.ctx.strokeStyle = "rgba(2, 6, 23, 0.72)"
      this.ctx.strokeText(name, x, y - radius - 7)
      this.ctx.fillStyle = "rgba(248, 250, 252, 0.96)"
      this.ctx.fillText(name, x, y - radius - 7)
    }
  },

  setStartLabel(text) {
    if (this.startLabel) this.startLabel.textContent = text
  },

  showError(message) {
    if (!this.errorEl) return
    this.errorEl.hidden = false
    this.errorEl.textContent = message
  },

  // Safari cannot re-ask for location: the user must change a Settings switch.
  // The gate's one line is not enough to act on, so show the steps in a modal
  // with a link into Settings. iOS Safari only (see `isIosSafari/1`).
  offerLocationSettings({retried = false} = {}) {
    // The sheet lives at page level, not inside #ar-root: it must show while
    // the gate is up and #ar-stage is hidden.
    const modal = document.querySelector("#ar-geo-denied")
    if (!modal) return
    if (
      !shouldOfferSettings({
        code: 1,
        iosSafari: isIosSafari(navigator),
        dismissedAt: this._geoDeniedDismissedAt,
        retried,
      })
    ) {
      return
    }

    // Consume the retry: it belongs to the Start tap that just failed, not to
    // every later watch error (a foreground revive would otherwise nag).
    this._geoDeniedRetry = false

    const link = modal.querySelector("#ar-geo-denied-settings")
    if (link) link.setAttribute("href", GEO_DENIED_SETTINGS_URL)

    modal.hidden = false
    modal.querySelector("#ar-geo-denied-close")?.focus()
    this._geoDeniedOpen = true
  },

  closeLocationSettings() {
    const modal = document.querySelector("#ar-geo-denied")
    if (!modal || modal.hidden) return
    modal.hidden = true
    this._geoDeniedOpen = false
    this._geoDeniedDismissedAt = Date.now()
    this.startBtn?.focus()
  },

  clearError() {
    if (!this.errorEl) return
    this.errorEl.hidden = true
    this.errorEl.textContent = ""
  },

  // Every step of `startExploring` can throw, and they need different advice:
  // camera, compass/motion and location are three separate Settings toggles.
  // Matching on "Permission denied" alone told a fan whose *compass* was
  // refused to go and enable the *camera* — measured 2026-10-26, when the
  // compass prompt was refused and the gate said "Camera permission denied".
  friendlyError(err) {
    const name = err && err.name
    const msg = err && err.message ? err.message : String(err)

    // Our own messages already name the setting; do not re-label them.
    if (/^(Compass|Location) permission denied/i.test(msg)) return msg
    if (name === "NotAllowedError" || /NotAllowedError|Permission denied/i.test(msg)) {
      return "Camera permission denied. Enable Camera for Safari in Settings."
    }
    if (name === "NotFoundError" || /NotFoundError|DevicesNotFound/i.test(msg)) {
      return "No rear camera found."
    }
    if (/HTTPS|secure/i.test(msg)) {
      return "HTTPS is required for camera and GPS on iPhone. Use the https:// address."
    }
    return msg
  },
}
